import { useState, useEffect, useMemo } from "react";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { Products } from "./components/products";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Testimonials } from "./components/testimonials";
import { Contact } from "./components/contact";
import { Chatbot } from "./components/chatbot";
import { FaAngleUp } from 'react-icons/fa';
import JsonData from "./data/data.json";
import TagManager from 'react-gtm-module';
import "./App.css";
import scroll from "./utils/scroll";
import ProductDetail from "./components/ProductDetail";
import ServiceDetail from "./components/ServiceDetail";
import BlogPage from "./components/BlogPage";
import { Routes, Route, useLocation } from "./router";
import { slugify } from "./utils/slugify";

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [showTopBtn, setShowTopBtn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  useEffect(() => {
    const header = document.getElementById("menu");
    const title = document.getElementById("menu-title");
    const link = document.getElementById("bs-navbar-collapse");
    // Order must match DOM order to correctly detect active section
    const sectionIds = ["header", "services", "about", "products", "blog", "testimonials", "contact"];
    // Sections with light backgrounds should use a dark navbar; others get a light navbar; top of page stays transparent
    const lightBackgroundSections = new Set(["products", "services", "blog", "contact"]);
    const isDetail = location.pathname.startsWith("/product/") || location.pathname.startsWith("/service/") || location.pathname.startsWith("/blog");
    if (!header || !title || !link) return;

    const getActiveSection = () => {
      const scrollPos = window.scrollY + 120; // offset to account for navbar height
      let current = "header";
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          if (scrollPos >= top) {
            current = id;
          }
        }
      });
      return current;
    };

    const applyNavScheme = (scheme) => {
      header.classList.remove("navbar-light", "navbar-dark", "navbar-transparent", "detail-page-nav");
      if (scheme === "transparent") {
        header.classList.add("navbar-transparent");
        header.style.background = "transparent";
        header.style.textShadow = "none";
        header.style.borderBottom = "none";
        updateLinkColor("#ffffff");
        title.style.color = "#ffffff";
        link.style.textShadow = "0px 0px rgba(0, 0, 0, 0.08)";
        return;
      }
      if (scheme === "dark-navbar") {
      header.classList.add("navbar-dark");
        header.style.background = "linear-gradient(180deg, #093946 0%, #398ba2 100%)";
        header.style.textShadow = "0 6px 24px rgba(0, 0, 0, 0.35)";
        updateLinkColor("#ffffff");
        title.style.color = "#ffffff";
        link.style.textShadow = "0px 0px rgba(0, 0, 0, 0.15)";
      } else {
        header.classList.add("navbar-light");
        header.style.background = "#ffffff";
        header.style.textShadow = "0 6px 16px rgba(0, 0, 0, 0.08)";
        updateLinkColor("#0c1221");
        title.style.color = "#0c1221";
        link.style.textShadow = "0px 0px rgba(0, 0, 0, 0.05)";
      }
    };

    const handleScroll = () => {
      const isScrolled = window.scrollY > 70;
      const isMobile = window.innerWidth <= 768;

      // Force dark navbar on detail pages regardless of scroll
      if (isDetail) {
        applyNavScheme("dark-navbar");
        header.classList.add("detail-page-nav");
        setShowTopBtn(isScrolled);
        return;
      }
      header.classList.remove("detail-page-nav");

      setShowTopBtn(isScrolled);

      const active = getActiveSection();
      const scheme = lightBackgroundSections.has(active) ? "dark-navbar" : "light-navbar";
      const atTop = window.scrollY < 10;

      if (isMobile) {
        if (atTop) {
          applyNavScheme("transparent");
        } else {
          applyNavScheme(scheme);
        }
        const menu = document.getElementById("bs-navbar-collapse");
        const toggle = document.querySelector(".navbar-toggle");
        if (menu && (menu.classList.contains("in") || menu.classList.contains("show"))) {
          menu.classList.remove("in");
          menu.classList.remove("show");
        }
        if (toggle && !toggle.classList.contains("collapsed")) {
          toggle.classList.add("collapsed");
          toggle.setAttribute("aria-expanded", "false");
        }
      } else {
        if (atTop) {
          applyNavScheme("transparent");
        } else {
          applyNavScheme(scheme);
        }
      }
    };

    const updateLinkColor = (color) => {
      // Update the link color here
      const links = document.querySelectorAll("#menu a");
      links.forEach((link) => {
        link.style.color = color;
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);

  const scrollToTop = () => {
    scroll.animateScroll(0);
  };

  const productsWithSlugs = useMemo(
    () => (landingPageData.Products || []).map((f) => ({ ...f, slug: slugify(f.title || f.name || "") })),
    [landingPageData]
  );
  const servicesWithSlugs = useMemo(
    () => (landingPageData.Services || []).map((s) => ({ ...s, slug: slugify(s.name || s.title || "") })),
    [landingPageData]
  );

  const Home = () => (
    <>
      <Header data={landingPageData.Header} />
      <Services data={servicesWithSlugs} />
      <About data={landingPageData.About} />
      <Products data={productsWithSlugs} />
      <Testimonials data={landingPageData.Testimonials} />
      <Contact data={landingPageData.Contact} />
    </>
  );

  useEffect(() => {
    if (!location.hash) return;
    const targetId = location.hash.replace("#", "");
    const el = document.getElementById(targetId);
    if (el) {
      setTimeout(() => {
        scroll.animateScroll(el, { offset: 10 });
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, location.pathname);
        }
      }, 10);
    }
  }, [location.hash, location.pathname]);

  return (
    <div>
      <Navigation />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/product/:slug" element={<ProductDetail products={productsWithSlugs} />} />
        <Route path="/service/:slug" element={<ServiceDetail services={servicesWithSlugs} />} />
        <Route path="/blog" element={<BlogPage />} />
      </Routes>
      <button onClick={scrollToTop} className={`back-to-top ${showTopBtn ? 'visible' : ''}`}>
        <FaAngleUp />
      </button>
      <Chatbot />
    </div>
  );
};

const tagManagerArgs = {
  gtmId: 'G-2F7PW5CNMK'
};
TagManager.initialize(tagManagerArgs);

export default App;
